import { useCallback } from 'react';
import {
  AudioFileFormat,
  ScriptFormat,
} from '../../../server/graphql/__generated.types';
import { useDownloadProjectClipsMutation } from './operation.generated';
import performDownload from './performDownload';
import {
  useEventsContext,
  EventName,
} from '../../context/events/EventsContext';
import getElapsedTimeMs from '../../util/getElapsedTimeMs';
import { toast, updateToast } from '../Toaster';
import { usePromptsContext } from '../../context/prompts/PromptsContext';
import handleEntitlementDownloadError from './handleEntitlementDownloadError';

const useDownloadProjectClips = () => {
  const [, dispatchPrompt] = usePromptsContext();
  const [download] = useDownloadProjectClipsMutation();
  const [eventTracking] = useEventsContext();

  const downloadProjectClips = useCallback(
    async (inputs: {
      projectId: string;
      audioFileFormat: AudioFileFormat;
      includeScriptFormats?: Array<ScriptFormat>;
    }) => {
      const { projectId, audioFileFormat, includeScriptFormats } = inputs;
      const startTime = performance.now();
      const errorMessage = 'Failed to download project clips';

      const toastId = toast({
        message: `Downloading project clips`,
        type: 'loading',
      });

      try {
        await download({
          variables: {
            inputs: {
              audioFileFormat,
              projectId,
              clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              includeScriptFormats,
            },
          },
          update: (proxy, { data }) => {
            if (!data?.downloadProjectClips.success) {
              const errorMsg =
                data?.downloadProjectClips.message ?? errorMessage;
              const errorCode = data?.downloadProjectClips.code;

              handleEntitlementDownloadError({
                errorCode,
                toastId,
                errorMessage: errorMsg,
                dispatchPrompt,
              });

              eventTracking.track({
                type: EventName.ProjectDownload,
                data: {
                  projectId,
                  audioFormat: audioFileFormat,
                  elapsedTimeMs: getElapsedTimeMs(startTime),
                  success: false,
                  error: errorMsg,
                  includeScriptFormats: includeScriptFormats ?? [],
                },
              });
            } else {
              const { data: downloadData } = data.downloadProjectClips;

              eventTracking.track({
                type: EventName.ProjectDownload,
                data: {
                  projectId,
                  audioFormat: audioFileFormat,
                  clipsCount:
                    downloadData?.downloadMetricsMetadata.numberOfClips ?? 0,
                  elapsedTimeMs: getElapsedTimeMs(startTime),
                  combinedTextLength:
                    downloadData?.downloadMetricsMetadata.combinedTextLength ??
                    0,
                  numberOfClipsJustConverted:
                    downloadData?.downloadMetricsMetadata
                      .numberOfClipsJustConverted ?? 0,
                  zipFileAlreadyExisted:
                    downloadData?.downloadMetricsMetadata
                      .zipFileAlreadyExisted ?? false,
                  success: true,
                  includeScriptFormats: includeScriptFormats ?? [],
                },
              });

              performDownload({
                fileFormat: downloadData!.fileFormat,
                friendlyFileName: downloadData!.friendlyFileName,
                signedURL: downloadData!.signedURL,
              });

              updateToast(toastId ?? '', {
                message: 'Successfully downloaded clips',
                type: 'success',
              });
            }
          },
        });
      } catch (error) {
        updateToast(toastId ?? '', {
          message: 'Failed to download clips',
          type: 'error',
        });
      }
    },
    [dispatchPrompt, download, eventTracking]
  );

  return downloadProjectClips;
};

export default useDownloadProjectClips;
